import React from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { Button } from '../../ui/Button';
import Link from 'next/link';

interface SuperAdminErrorFallbackProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retry?: () => void;
}

/**
 * Super Admin-specific error fallback UI
 */
const SuperAdminErrorFallback: React.FC<SuperAdminErrorFallbackProps> = ({ error, retry }) => {
  const handleGoToDashboard = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/super-admin';
    }
  };

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/super-admin/login';
    }
  };

  const handleGoToPublic = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-red-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <div className="text-center">
            {/* Super Admin Error Icon */}
            <div className="mx-auto h-16 w-16 text-red-600 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Super Admin System Error
            </h2>

            <p className="text-gray-600 mb-6 text-sm">
              A critical error occurred in the super admin system. This may affect
              system administration and organization management.
            </p>

            {/* Critical Alert */}
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">
                    <strong>Critical System Impact:</strong> This error may affect
                    multi-tenant operations and organization management.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mb-6">
              {retry && (
                <Button
                  onClick={retry}
                  variant="solid"
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  Retry Operation
                </Button>
              )}

              <Button
                onClick={handleGoToDashboard}
                variant="secondary"
                className="w-full"
              >
                Go to Super Admin Dashboard
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Logout
                </Button>

                <Button
                  onClick={handleGoToPublic}
                  variant="outline"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  Public Site
                </Button>
              </div>
            </div>

            {/* System Tools */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">System Tools:</h3>
              <div className="space-y-2 text-sm">
                <Link
                  href="/super-admin/debug-provisioning"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Debug Provisioning
                </Link>
                <Link
                  href="/super-admin/analytics"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  System Analytics
                </Link>
                <Link
                  href="/super-admin/organisations"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Organization Management
                </Link>
              </div>
            </div>

            {/* Error Details for Development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left bg-red-50 p-4 rounded text-xs border border-red-200">
                <summary className="cursor-pointer font-medium text-red-800">
                  Critical Error Details (Development Only)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-red-700">
                  {error.message}
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Emergency Contact */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p className="mb-1">
                <strong>🚨 Emergency Contact:</strong>
              </p>
              <p>
                If this affects production systems, contact the development team immediately at{' '}
                <a
                  href="mailto:emergency@dramatickets.co.uk"
                  className="text-red-600 hover:text-red-800 hover:underline font-medium"
                >
                  emergency@dramatickets.co.uk
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Super Admin Error Boundary Component
 * Specialized for super admin system error handling
 */
export const SuperAdminErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={SuperAdminErrorFallback}
      onError={(error, errorInfo) => {
        // Log super admin critical errors
        console.error('[SuperAdminErrorBoundary] CRITICAL Super Admin Error:', error);
        console.error('[SuperAdminErrorBoundary] Error info:', errorInfo);

        // Send to monitoring service with critical priority
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'exception', {
            description: `CRITICAL Super Admin Error: ${error.message}`,
            fatal: true,
            custom_map: { super_admin_error: true, critical: true }
          });
        }

        // Could also send to critical monitoring/alerting service
        // alertCriticalSystemError(error, { context: 'super_admin', severity: 'critical' });
      }}
      errorTitle="Critical System Error"
      errorMessage="A critical error occurred in the super admin system. Immediate attention required."
      maxRetries={1} // Minimal retries for critical errors
      showErrorDetails={true}
    >
      {children}
    </ErrorBoundary>
  );
};
